import React, { useState, useEffect } from 'react';
import { Map, Mail, Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const Login = () => {
  const navigate = useNavigate();
  const { checkAuth, isAuthenticated, isOwner, isInvestor } = useAuth();
  const [mode, setMode] = useState('choice'); // 'choice', 'email', 'set-password', 'forgot-password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Check URL params for reset token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlMode = params.get('mode');
    const urlToken = params.get('token');
    const resetParam = params.get('reset');
    if (urlMode === 'reset' && urlToken) {
      setResetToken(urlToken);
      setMode('forgot-password');
    } else if (resetParam) {
      setResetToken(resetParam);
      setMode('forgot-password');
      setResetEmailSent(true); // Skip the "enter email" step — go straight to new password
    }
  }, []);

  // Auto-redirect if already authenticated (e.g., localStorage token from a previous session)
  useEffect(() => {
    if (isAuthenticated) {
      navigate(isInvestor ? '/investor' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, isInvestor, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/auth/login', { email, password });
      if (response.data.success) {
        // Store session token in localStorage (critical for mobile)
        if (response.data.token) {
          localStorage.setItem('session_token', response.data.token);
        }
        // Force auth context to re-check with the new token
        const userData = await checkAuth(true);
        toast.success('Login successful');
        // Redirect based on role
        const role = userData?.role || response.data.user?.role;
        if (role === 'investor') {
          navigate('/investor', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      if (detail.includes('No password set')) {
        toast.error('No password set for this account. Please set a password first.');
        setMode('set-password');
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/set-password', { email, password });
      toast.success('Password set successfully! You can now log in.');
      setMode('email');
      setConfirmPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to set password';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('If this email is registered, you will receive reset instructions.');
      setResetEmailSent(true);
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetToken || !password) {
      toast.error('Please enter the reset token and new password');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, new_password: password });
      toast.success('Password reset successfully! You can now log in.');
      setMode('email');
      setResetToken('');
      setConfirmPassword('');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to reset password';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (mode) {
      case 'email':
        return (
          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-sm"
                  data-testid="email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-sm"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex justify-between items-center text-sm">
              <button
                type="button"
                onClick={() => setMode('set-password')}
                className="text-forest-600 hover:underline"
              >
                First time? Set password
              </button>
              <button
                type="button"
                onClick={() => setMode('forgot-password')}
                className="text-forest-600 hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm py-5"
              data-testid="email-login-btn"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
            <button
              type="button"
              onClick={() => setMode('choice')}
              className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Back to options
            </button>
          </form>
        );

      case 'set-password':
        return (
          <form onSubmit={handleSetPassword} className="space-y-4">
            <p className="text-sm text-muted-foreground text-center mb-4">
              Set up a password for your authorized email address
            </p>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm py-5"
            >
              {loading ? 'Setting password...' : 'Set Password'}
            </Button>
            <button
              type="button"
              onClick={() => setMode('email')}
              className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </form>
        );

      case 'forgot-password':
        return (
          <form onSubmit={resetToken ? handleResetPassword : (resetEmailSent ? (e) => { e.preventDefault(); } : handleForgotPassword)} className="space-y-4">
            <p className="text-sm text-muted-foreground text-center mb-4">
              {resetToken ? 'Enter your new password' : resetEmailSent ? 'Check your email for the reset code' : 'Enter your email to reset your password'}
            </p>
            {!resetToken && !resetEmailSent ? (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rounded-sm"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
            ) : !resetToken && resetEmailSent ? (
              <div className="space-y-4">
                <div className="p-3 bg-green-50 border border-green-200 rounded-sm text-sm text-green-700 text-center">
                  We sent a reset code to <strong>{email}</strong>. Check your inbox.
                </div>
                <div className="space-y-2">
                  <Label htmlFor="resetToken">Reset Code</Label>
                  <Input
                    id="resetToken"
                    type="text"
                    placeholder="Paste the reset code from your email"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="rounded-sm font-mono text-xs"
                    data-testid="reset-token-input"
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="resetToken">Reset Code</Label>
                  <Input
                    id="resetToken"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="rounded-sm font-mono text-xs"
                    data-testid="reset-token-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="At least 8 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 rounded-sm"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="rounded-sm"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
              </>
            )}
            <Button
              type="submit"
              disabled={loading || (resetEmailSent && !resetToken)}
              onClick={resetEmailSent && !resetToken ? undefined : undefined}
              className="w-full bg-forest-500 hover:bg-forest-600 text-white rounded-sm py-5"
              data-testid="forgot-password-submit-btn"
            >
              {loading ? 'Processing...' : resetToken ? 'Reset Password' : resetEmailSent ? 'Waiting for code...' : 'Send Reset Code'}
            </Button>
            {resetEmailSent && !resetToken && (
              <button
                type="button"
                onClick={() => { setResetEmailSent(false); }}
                className="w-full text-sm text-forest-600 hover:underline"
              >
                Didn't receive the email? Send again
              </button>
            )}
            <button
              type="button"
              onClick={() => { setMode('email'); setResetToken(''); setResetEmailSent(false); }}
              className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          </form>
        );

      default: // 'choice'
        return (
          <div className="space-y-4">
            <Button
              onClick={handleGoogleLogin}
              className="w-full bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-sm py-6 text-base"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-muted-foreground">or</span>
              </div>
            </div>

            <Button
              onClick={() => setMode('email')}
              variant="outline"
              className="w-full rounded-sm py-6 text-base"
              data-testid="email-option-btn"
            >
              <Mail className="w-5 h-5 mr-3" />
              Sign in with Email
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-forest-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-sm shadow-xl p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 bg-terracotta rounded-sm flex items-center justify-center">
              <Map className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl text-forest-900">LandFlow</h1>
              <p className="text-xs text-muted-foreground">Land Investment Operating System</p>
            </div>
          </div>

          {/* Welcome text */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {mode === 'choice' ? 'Welcome Back' : 
               mode === 'set-password' ? 'Set Your Password' :
               mode === 'forgot-password' ? 'Reset Password' : 'Sign In'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {mode === 'choice' ? 'Sign in to access your land investment dashboard' :
               mode === 'set-password' ? 'Create a password for your account' :
               mode === 'forgot-password' ? 'We\'ll help you recover access' : 'Enter your credentials'}
            </p>
          </div>

          {/* Dynamic content based on mode */}
          {renderContent()}

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            By signing in, you agree to our terms of service
          </p>
        </div>

        <p className="text-center text-xs text-white/40 mt-4">
          v1.0.0 • LandFlow
        </p>
      </div>
    </div>
  );
};
